import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import { dialog } from '../../store/dialogStore';

const PALETTE = [
  '#00c875', '#00a884', '#037f4c', '#0086c0', '#579bfc', '#a25ddc',
  '#9d50dd', '#c4c4c4', '#808080', '#fdab3d', '#ffcb00', '#ff642e',
  '#e44258', '#df2f4a', '#ff158a', '#bb3354', '#7f5347', '#757575',
];

function ColorPicker({ value, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-6 h-6 rounded border border-white/10 shrink-0"
        style={{ backgroundColor: value }}
      />
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 top-full left-0 mt-1 bg-card border border-border rounded-lg p-2 shadow-xl grid grid-cols-6 gap-1 w-[168px]">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { onPick(c); setOpen(false); }}
                className="w-6 h-6 rounded hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function EditSelectOptionsModal({ column, onClose }) {
  const { updateColumn } = useBoardStore();
  const initial = Array.isArray(column.config?.options)
    ? column.config.options.map((o, i) =>
        typeof o === 'string'
          ? { label: o, color: PALETTE[i % PALETTE.length] }
          : { label: o.label || '', color: o.color || PALETTE[i % PALETTE.length] }
      )
    : [];
  const [options, setOptions] = useState(initial);

  const commit = (next) => {
    setOptions(next);
    updateColumn(column.id, { config: { ...(column.config || {}), options: next } });
  };

  const handleAdd = () => commit([...options, { label: 'Nova opção', color: PALETTE[options.length % PALETTE.length] }]);
  const handleRemove = async (i) => {
    if (!(await dialog.confirm(`Remover "${options[i].label}"`, 'Esta opção será removida.', { confirmLabel: 'Remover', danger: true }))) return;
    commit(options.filter((_, idx) => idx !== i));
  };
  const handleLabel = (i, label) => {
    const next = [...options];
    next[i] = { ...next[i], label };
    commit(next);
  };
  const handleColor = (i, color) => {
    const next = [...options];
    next[i] = { ...next[i], color };
    commit(next);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[420px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <h3 className="text-sm font-semibold text-text-primary">
            Editar opções — {column.name}
          </h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <ColorPicker value={opt.color} onPick={(c) => handleColor(i, c)} />
                <input
                  defaultValue={opt.label}
                  onBlur={(e) => handleLabel(i, e.target.value.trim() || opt.label)}
                  onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                  className="flex-1 bg-main border border-border/50 rounded px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50"
                />
                <button
                  onClick={() => handleRemove(i)}
                  className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors"
                  title="Remover"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {options.length === 0 && (
              <div className="text-center py-6 text-sm text-text-secondary">
                Nenhuma opção ainda.
              </div>
            )}
          </div>
          <button
            onClick={handleAdd}
            className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-accent border border-dashed border-accent/40 rounded-lg hover:bg-accent/5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nova opção
          </button>
        </div>
        <div className="px-5 py-3 border-t border-border/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white bg-accent hover:bg-accent/80 rounded-lg transition-colors"
          >
            Concluído
          </button>
        </div>
      </div>
    </div>
  );
}
